import { useState } from 'react';
import { supabase } from '../AuthProvider';
import { useAuth } from '../../../hooks/useAuth';
import posthog from 'posthog-js';
import Logger from '@app/utils/logger';

export interface UserInfoFormData {
  displayName: string;
  country: string;
  howFoundUs: string;
  howFoundUsOther: string;
  userType: 'private' | 'business' | '';
  companyName: string;
  roleDescription: string;
  companySegment: string;
  companySegmentOther: string;
  useCase: string[];
  useCaseOther: string;
  goals: string[];
  goalsOther: string;
}

export interface InitialUserData {
  displayName?: string;
  country?: string;
  howFoundUs?: string;
  howFoundUsOther?: string;
  userType?: 'private' | 'business' | '';
  companyName?: string;
  roleDescription?: string;
  companySegment?: string;
  companySegmentOther?: string;
  useCase?: string[];
  useCaseOther?: string;
  goals?: string[];
  goalsOther?: string;
}

export function useUserInfoForm(initialData: InitialUserData = {}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<UserInfoFormData>({
    displayName: initialData.displayName || '',
    country: initialData.country || '',
    howFoundUs: initialData.howFoundUs || '',
    howFoundUsOther: initialData.howFoundUsOther || '',
    userType: initialData.userType || '',
    companyName: initialData.companyName || '',
    roleDescription: initialData.roleDescription || '',
    companySegment: initialData.companySegment || '',
    companySegmentOther: initialData.companySegmentOther || '',
    useCase: initialData.useCase || [],
    useCaseOther: initialData.useCaseOther || '',
    goals: initialData.goals || [],
    goalsOther: initialData.goalsOther || '',
  });

  const [loading, setLoading] = useState(false);
  const { refreshUser } = useAuth();

  const updateField = (field: keyof UserInfoFormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Validate step 1 fields
  const validateStep1 = (): string | null => {
    if (!formData.displayName.trim()) {
      return 'Display name is required';
    }

    if (!formData.country) {
      return 'Country is required';
    }

    return null;
  };

  // Validate step 2 fields
  const validateStep2 = (): string | null => {
    if (!formData.howFoundUs) {
      return 'Please tell us how you found us';
    }

    // Note: 'Other' text field is optional - users can select 'Other' without specifying

    return null;
  };

  // Validate step 3 fields
  const validateStep3 = (): string | null => {
    if (!formData.userType) {
      return 'User type is required';
    }

    if (formData.userType === 'business' && !formData.companyName.trim()) {
      return 'Company name is required for business accounts';
    }

    if (formData.userType === 'business' && !formData.companySegment) {
      return 'Please select your company segment';
    }

    // Note: 'Other' company segment text field is optional - users can select 'Other' without specifying

    return null;
  };

  // Validate step 4 fields
  const validateStep4 = (): string | null => {
    if (!formData.roleDescription) {
      return 'Role description is required';
    }

    if (formData.useCase.length === 0) {
      return 'Please select at least one use case';
    }

    // Note: 'Other' use case text field is optional - users can select 'Other' without specifying

    if (formData.goals.length === 0) {
      return 'Please select at least one goal';
    }

    // Note: 'Other' goals text field is optional - users can select 'Other' without specifying

    return null;
  };

  const validateForm = (): string | null => {
    const step1Error = validateStep1();
    if (step1Error) return step1Error;

    const step2Error = validateStep2();
    if (step2Error) return step2Error;

    const step3Error = validateStep3();
    if (step3Error) return step3Error;

    const step4Error = validateStep4();
    if (step4Error) return step4Error;

    return null;
  };

  // Check if current step is valid
  const isCurrentStepValid =
    currentStep === 1
      ? formData.displayName.trim() && formData.country
      : currentStep === 2
        ? formData.howFoundUs // 'Other' text field is optional
        : currentStep === 3
          ? formData.userType &&
            (formData.userType === 'private' || formData.companyName.trim()) &&
            (formData.userType === 'private' || !!formData.companySegment) // 'Other' company segment text is optional
          : formData.roleDescription && formData.useCase.length > 0 && formData.goals.length > 0; // 'Other' text fields are optional

  const isFormValid =
    formData.displayName.trim() &&
    formData.country &&
    formData.howFoundUs && // 'Other' text field is optional
    formData.userType &&
    (formData.userType === 'private' || formData.companyName.trim()) &&
    (formData.userType === 'private' || !!formData.companySegment) && // 'Other' company segment text is optional
    formData.roleDescription &&
    formData.useCase.length > 0 &&
    formData.goals.length > 0; // 'Other' text fields are optional

  const nextStep = () => {
    if (!isCurrentStepValid) return;
    if (currentStep === 1) setCurrentStep(2);
    else if (currentStep === 2) setCurrentStep(3);
    else if (currentStep === 3) setCurrentStep(4);
  };

  const prevStep = () => {
    if (currentStep === 2) setCurrentStep(1);
    else if (currentStep === 3) setCurrentStep(2);
    else if (currentStep === 4) setCurrentStep(3);
  };

  const submitForm = async (): Promise<string | null> => {
    const validationError = validateForm();
    if (validationError) {
      return validationError;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          full_name: formData.displayName.trim(),
          country: formData.country,
          how_found_us: formData.howFoundUs,
          how_found_us_other: formData.howFoundUs === 'Other' ? formData.howFoundUsOther : null,
          user_type: formData.userType,
          company_name: formData.userType === 'business' ? formData.companyName.trim() : null,
          company_segment: formData.userType === 'business' ? formData.companySegment : null,
          company_segment_other:
            formData.userType === 'business' && formData.companySegment === 'Other'
              ? formData.companySegmentOther
              : null,
          role_description: formData.roleDescription,
          use_case: formData.useCase,
          use_case_other: formData.useCase.includes('Other') ? formData.useCaseOther : null,
          goals: formData.goals,
          goals_other: formData.goals.includes('Other') ? formData.goalsOther : null,
        },
      });

      if (error) {
        throw error;
      }

      try {
        posthog.setPersonProperties({
          full_name: formData.displayName.trim(),
          country: formData.country,
          how_found_us: formData.howFoundUs,
          how_found_us_other: formData.howFoundUs === 'Other' ? formData.howFoundUsOther : null,
          user_type: formData.userType,
          company_name: formData.userType === 'business' ? formData.companyName.trim() : null,
          company_segment: formData.userType === 'business' ? formData.companySegment : null,
          company_segment_other:
            formData.userType === 'business' && formData.companySegment === 'Other'
              ? formData.companySegmentOther
              : null,
          role_description: formData.roleDescription,
          use_case: formData.useCase,
          use_case_other: formData.useCase.includes('Other') ? formData.useCaseOther : null,
          goals: formData.goals,
          goals_other: formData.goals.includes('Other') ? formData.goalsOther : null,
        });
      } catch (error) {
        Logger.error('Error updating user properties on PostHog', error);
      }

      // Refresh user data to update the auth context
      await refreshUser();

      return null; // Success
    } catch (err) {
      return err instanceof Error ? err.message : 'Failed to save user information';
    } finally {
      setLoading(false);
    }
  };

  return {
    currentStep,
    formData,
    loading,
    isCurrentStepValid,
    isFormValid,
    updateField,
    nextStep,
    prevStep,
    submitForm,
  };
}
